import type { Project } from '@/api/client';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { getReadyByMonthBucketFromProject } from '@/utils/readyMonthStats';

export type ProjectTableOrderContext = {
  /** Ключи месяцев «Готов» в порядке API `ready-months` (старые первыми). */
  readyMonthKeysInOrder: string[];
  /** Список id по месяцу, если месяц уже подгружен; иначе сравнение по датам сдачи. */
  readyProjectIdsByMonth: Record<string, string[]>;
  activeProjectIds: string[];
  archivedProjectIds: string[];
  resolveProject: (id: string) => Project | undefined;
};

function earliestCompletedMs(project: Project): number {
  let best = Infinity;
  for (const e of project.status_events ?? []) {
    if (e.event_type !== 'project_completed') continue;
    const ms = Date.parse(e.occurred_at);
    if (!Number.isNaN(ms)) best = Math.min(best, ms);
  }
  if (best !== Infinity) return best;
  if (project.completed_at) {
    const ms = Date.parse(project.completed_at);
    if (!Number.isNaN(ms)) return ms;
  }
  return Infinity;
}

function parseUpdatedMs(project: Project): number {
  const u = project.updated_at;
  if (!u) return 0;
  const t = Date.parse(u);
  return Number.isNaN(t) ? 0 : t;
}

type BucketClass = 'missing' | 'ready' | 'active' | 'archived';

function classifyForTableOrder(p: Project | undefined): BucketClass {
  if (!p) return 'missing';
  if (p.is_archived) return 'archived';
  if (isProjectExcludedFromActiveTable(p.state)) return 'ready';
  return 'active';
}

function categoryRank(c: BucketClass): number {
  if (c === 'ready') return 0;
  if (c === 'active') return 1;
  if (c === 'archived') return 2;
  return 3;
}

/**
 * Порядок id как у строк проектов в таблице без поиска:
 * готовые по месяцам (как в блоках месяца) → «в процессе» (как в active list) → архив.
 */
export function sortProjectIdsLikeTableOrder(ids: string[], ctx: ProjectTableOrderContext): string[] {
  const knownMonthIndex = Object.fromEntries(ctx.readyMonthKeysInOrder.map((k, i) => [k, i] as const));

  const monthSortKey = (project: Project): number => {
    const bucket = getReadyByMonthBucketFromProject(project);
    if (!bucket) return 9_999_999;
    const key = `${bucket.year}-${String(bucket.month).padStart(2, '0')}`;
    if (key in knownMonthIndex) return knownMonthIndex[key]!;
    return 1_000_000 + bucket.year * 12 + bucket.month;
  };

  const compare = (ida: string, idb: string): number => {
    const pa = ctx.resolveProject(ida);
    const pb = ctx.resolveProject(idb);
    const ca = classifyForTableOrder(pa);
    const cb = classifyForTableOrder(pb);
    const ra = categoryRank(ca);
    const rb = categoryRank(cb);
    if (ra !== rb) return ra - rb;

    if (ca === 'missing' || cb === 'missing') {
      if (ca === 'missing' && cb === 'missing') return 0;
      return ca === 'missing' ? 1 : -1;
    }

    if (ca === 'ready' && cb === 'ready') {
      const mkA = monthSortKey(pa!);
      const mkB = monthSortKey(pb!);
      if (mkA !== mkB) return mkA - mkB;

      const bucketA = getReadyByMonthBucketFromProject(pa!);
      const keyA = bucketA ? `${bucketA.year}-${String(bucketA.month).padStart(2, '0')}` : '';
      const list = keyA ? ctx.readyProjectIdsByMonth[keyA] : undefined;
      const ia = list?.indexOf(ida) ?? -1;
      const ib = list?.indexOf(idb) ?? -1;
      if (ia >= 0 && ib >= 0 && ia !== ib) return ia - ib;
      if (ia >= 0 && ib < 0) return -1;
      if (ia < 0 && ib >= 0) return 1;

      const ea = earliestCompletedMs(pa!);
      const eb = earliestCompletedMs(pb!);
      if (ea !== eb) return ea - eb;
      return parseUpdatedMs(pb!) - parseUpdatedMs(pa!);
    }

    if (ca === 'active' && cb === 'active') {
      const ia = ctx.activeProjectIds.indexOf(ida);
      const ib = ctx.activeProjectIds.indexOf(idb);
      const sa = ia >= 0 ? ia : 999_999;
      const sb = ib >= 0 ? ib : 999_999;
      if (sa !== sb) return sa - sb;
      return parseUpdatedMs(pb!) - parseUpdatedMs(pa!);
    }

    if (ca === 'archived' && cb === 'archived') {
      const ia = ctx.archivedProjectIds.indexOf(ida);
      const ib = ctx.archivedProjectIds.indexOf(idb);
      const sa = ia >= 0 ? ia : 999_999;
      const sb = ib >= 0 ? ib : 999_999;
      if (sa !== sb) return sa - sb;
      return parseUpdatedMs(pb!) - parseUpdatedMs(pa!);
    }

    return 0;
  };

  return [...ids].sort(compare);
}
